/**
 * Устройства подписки и отзыв доступа.
 *
 * Список приходит из панели: устройство появляется после первого
 * подключения VPN-клиента. Отключённое устройство перестаёт получать
 * подписку и освобождает место под новое.
 */
import { popup } from '@telegram-apps/sdk-react';

import { useDevices, useRevokeDevice } from '@/entities/subscription/queries';
import type { Device } from '@/shared/api/schemas';
import { formatShortDate } from '@/shared/lib/format';
import { Card } from '@/shared/ui/kit/Card';
import { Row } from '@/shared/ui/kit/Row';

import styles from './DeviceList.module.css';

async function confirmRevoke(device: Device): Promise<boolean> {
  const message = `${device.title} перестанет подключаться к VPN. Чтобы вернуть его, добавьте подписку заново.`;
  if (popup.open.isAvailable()) {
    const choice = await popup.open({
      title: 'Отключить устройство?',
      message,
      buttons: [
        { id: 'revoke', type: 'destructive', text: 'Отключить' },
        { id: 'cancel', type: 'cancel' },
      ],
    });
    return choice === 'revoke';
  }
  return window.confirm(`Отключить устройство?\n\n${message}`);
}

function subtitle(device: Device): string {
  const system = [device.platform, device.os_version].filter(Boolean).join(' ');
  return [system, device.app, `с ${formatShortDate(device.added_at)}`].filter(Boolean).join(' · ');
}

export function DeviceList({ subscriptionId, active }: { subscriptionId: string; active: boolean }) {
  const { data, isLoading, error, refetch } = useDevices(subscriptionId, active);
  const revoke = useRevokeDevice(subscriptionId);

  if (!active) {
    return (
      <Card>
        <Row icon="device" title="Устройства" subtitle="Появятся, когда подписка будет действовать" disabled />
      </Card>
    );
  }
  if (isLoading) {
    return (
      <Card>
        <Row icon="device" title="Загружаем устройства…" disabled />
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card>
        <Row icon="device" title="Не удалось загрузить устройства" subtitle="Нажмите, чтобы повторить" onClick={() => void refetch()} />
      </Card>
    );
  }
  if (data.devices.length === 0) {
    return (
      <Card>
        <Row
          icon="device"
          title="Пока нет устройств"
          subtitle="Устройство появится здесь после первого подключения к VPN"
          disabled
        />
      </Card>
    );
  }

  const onRevoke = async (device: Device) => {
    if (revoke.isPending || !(await confirmRevoke(device))) return;
    revoke.mutate(device.hwid);
  };

  return (
    <>
      <Card>
        {data.devices.map((device) => (
          <Row
            key={device.hwid}
            icon="device"
            title={device.title}
            subtitle={subtitle(device)}
            value={
              <button
                type="button"
                className={styles.revoke}
                disabled={revoke.isPending}
                onClick={() => void onRevoke(device)}
              >
                Отключить
              </button>
            }
          />
        ))}
      </Card>
      {revoke.error ? <p className={styles.error}>Не получилось отключить устройство. Попробуйте ещё раз.</p> : null}
      {data.limit !== null && data.devices.length >= data.limit ? (
        <p className={styles.hint}>
          Достигнут лимит устройств. Чтобы подключить новое, отключите одно из старых.
        </p>
      ) : null}
    </>
  );
}
